import { useState, useEffect } from 'react';
import apiClient from '../services/axiosInterceptor';
import { useSchoolDetailsStore } from './useSchoolDetailsStore';

export interface TimeSlotOption {
  label: string;
  value: string;
  id: string;
  startTime: string;
  endTime: string;
  slotType: string;
  periodNumber: number;
}

interface TimeSlotResponse {
  id: string; // Mongo ObjectID hex string, not a number
  timetable_type_id: string;
  period_number: number;
  period_label: string;
  start_time: string;
  end_time: string;
  slot_type: string;
}

interface TimeSlotsApiResponse {
  status: 'success' | 'error';
  slots: TimeSlotResponse[]; // GetTimeSlots (Go) returns the array under
                             // `slots`, not `data`
  message?: string;
}

export const useTimeSlots = (timetableTypeId?: string | null) => {
  const [timeSlots, setTimeSlots] = useState<TimeSlotOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // GetTimeSlots doesn't strictly require session_id, but without it you
  // get slots across every session for this timetable type — scope it to
  // the current one, same as TimeSlotsManager / ViewTimeSlotsModal.
  const currentSessionId = useSchoolDetailsStore((state) => state.schoolDetails?.current_session?.id);

  useEffect(() => {
    const fetchTimeSlots = async () => {
      // Don't fetch if no timetableTypeId or no current session yet
      if (!timetableTypeId || !currentSessionId) {
        setTimeSlots([]);
        setLoading(false);
        setError(null);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        // Route is `/timeslots` (RegisterTimeSlotRoutes: r.Group("/timeslots")),
        // not `/get-time-slots` — that mismatch alone 404s regardless of params.
        const response = await apiClient.get<TimeSlotsApiResponse>('timeslots', {
          params: {
            timetable_type_id: timetableTypeId,
            session_id: currentSessionId,
            limit: 100,
            page: 1,
          },
        });

        if (response.data.status === 'success') {
          const slots = response.data.slots || [];
          if (slots.length === 0) {
            setTimeSlots([]);
            setError('No time slots available for this timetable type');
          } else {
            const options: TimeSlotOption[] = slots
              .sort((a, b) => a.period_number - b.period_number)
              .map((slot) => ({
                label: `${slot.period_label} (${slot.start_time} - ${slot.end_time})`,
                value: slot.id,
                id: slot.id,
                startTime: slot.start_time,
                endTime: slot.end_time,
                slotType: slot.slot_type,
                periodNumber: slot.period_number,
              }));

            setTimeSlots(options);
          }
        } else {
          setError(response.data.message || 'Failed to load time slots.');
        }
      } catch (err: any) {
        const message =
          err?.response?.data?.message ||
          err?.message ||
          'An error occurred while fetching time slots.';
        setError(message);
      } finally {
        setLoading(false);
      }
    };

    fetchTimeSlots();
  }, [timetableTypeId, currentSessionId]);

  return {
    timeSlots,
    loading,
    error,
  };
};